import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Home, LogOut, Settings, UtensilsCrossed, Star, Gamepad2, ListMusic,
  MoreHorizontal, X, CalendarDays, ShoppingBag, Users, BarChart3,
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import PlayerBar from './PlayerBar';
import { ErrorBoundary } from './ErrorBoundary';

const SPIN_LOGO = '/brand/mb-icon.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ children, hidePlayerBarBelowXl = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [petpoojaOn, setPetpoojaOn] = useState(false);

  // Gate the "Orders" nav item on the host's actual Petpooja toggle state.
  // We fetch once per mount + user change; changes to Petpooja settings
  // update /petpooja/config which is re-read on next navigation.
  useEffect(() => {
    let cancelled = false;
    if (!user?.features?.menus) { setPetpoojaOn(false); return () => {}; }
    axios.get(`${API}/petpooja/config`)
      .then((r) => { if (!cancelled) setPetpoojaOn(!!r.data?.is_connected); })
      .catch(() => { if (!cancelled) setPetpoojaOn(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.features?.menus]);

  // ===== Feb 2026 (production stall RCA) =====
  // Listen for auth-lost events fired by the API interceptor when
  // /auth/refresh 401s while MusicKit is actively playing. Instead of
  // yanking the tab to /login mid-set, surface a persistent toast that
  // links to the re-auth flow — the DJ finishes the set, then re-logs in
  // on their own terms.
  useEffect(() => {
    const onAuthLost = () => {
      toast.error('Session expired — playback continues. Tap here to sign in again.', {
        id: 'auth-lost-persistent',
        duration: Infinity,
        action: {
          label: 'Sign in',
          onClick: () => { window.location.replace('/login'); },
        },
      });
    };
    window.addEventListener('musicbox:auth-lost', onAuthLost);
    return () => window.removeEventListener('musicbox:auth-lost', onAuthLost);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAI = user?.client_type === 'ai';
  const allNav = [
    { to: '/', icon: Home, label: 'Home' },
    // Playlists retired for AI venues — the AI booth streams from the
    // in-house library on its own scheduler, so venue-curated playlists
    // don't apply and the "Now Spinning · No playlists scheduled" surface
    // just confuses AI operators.
    ...(!isAI ? [{ to: '/playlists', icon: ListMusic, label: 'Playlists' }] : []),
    { to: '/guests', icon: Users, label: 'Guests' },
    ...(user?.features?.menus ? [{ to: '/menu', icon: UtensilsCrossed, label: 'Menu' }] : []),
    ...(petpoojaOn ? [{ to: '/orders', icon: ShoppingBag, label: 'Orders' }] : []),
    ...(user?.features?.reviews !== false ? [{ to: '/reviews', icon: Star, label: 'Reviews' }] : []),
    ...(user?.features?.games ? [{ to: '/games', icon: Gamepad2, label: 'Games' }] : []),
    ...(user?.features?.events ? [{ to: '/events', icon: CalendarDays, label: 'Events' }] : []),
    // Analytics — the old venue-dashboard metrics live here as of Feb 2026.
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  // First 4 items get bottom-tab slots on phone. Rest go in "More" drawer.
  const phoneTopNav = allNav.slice(0, 4);
  const phoneMoreNav = allNav.slice(4);

  return (
    <div className="h-screen flex flex-col bg-[#000000] overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        {/* ===== Sidebar (md+ = icon rail, lg+ = full) ===== */}
        <aside
          className="hidden md:flex md:w-20 lg:w-64 bg-[#000000] border-r border-white/5 flex-col flex-shrink-0"
          data-testid="venue-sidebar"
        >
          {/* Logo */}
          <div className="p-4 lg:p-6 flex flex-col items-center justify-center relative">
            <div className="absolute -bottom-2 -left-2 w-16 h-16 lg:w-24 lg:h-24 rounded-full bg-[#FF007F] opacity-20 blur-3xl pointer-events-none" />
            <div className="absolute -top-2 -right-2 w-16 h-16 lg:w-24 lg:h-24 rounded-full bg-[#39FF14] opacity-15 blur-3xl pointer-events-none" />
            <img
              src={SPIN_LOGO}
              alt="MusicBooth"
              className="relative w-12 h-12 lg:w-24 lg:h-24 object-contain"
              draggable={false}
            />
            <div className="hidden lg:block text-center mt-2 relative">
              <p className="text-white text-xl font-extrabold tracking-tight leading-none" style={{ fontFamily: 'Outfit' }}>
                MusicBooth
              </p>
              <p className="text-[9px] uppercase tracking-[0.28em] text-gray-500 mt-1 font-mono">
                by <span className="bg-gradient-to-r from-[#39FF14] to-[#00BFFF] bg-clip-text text-transparent font-semibold">MusicBooth</span>
              </p>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-2 lg:px-4 overflow-y-auto">
            <ul className="space-y-1">
              {allNav.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-2.5 lg:py-3 rounded-xl text-sm font-medium transition-colors
                      justify-center lg:justify-start
                      ${isActive ? 'bg-[#FF007F]/15 text-white ring-1 ring-[#FF007F]/30' : 'text-gray-400 hover:text-white hover:bg-white/5'}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    title={item.label}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden lg:inline">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User */}
          <div className="p-2 lg:p-4 border-t border-white/5">
            <div className="hidden lg:flex items-center gap-3 p-3 rounded-xl bg-white/5 mb-2">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-[#1A1A1A] text-white">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                {user?.is_zone_device && (
                  <p className="text-[10px] uppercase tracking-wider text-[#00FFFF] truncate" data-testid="zone-badge">
                    Zone · {user.zone_name}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-center lg:justify-start gap-3 px-3 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl"
              data-testid="logout-button"
              title="Log out"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="hidden lg:inline text-sm">Log out</span>
            </Button>
          </div>
        </aside>

        {/* ===== Mobile (<md) top bar ===== */}
        <header
          className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-black/90 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-4"
          data-testid="venue-mobile-header"
        >
          <div className="flex items-center gap-2">
            <img src={SPIN_LOGO} alt="MusicBooth" className="w-8 h-8 object-contain" draggable={false} />
            <span className="font-bold text-white text-sm tracking-wider" style={{ fontFamily: 'Outfit' }}>MusicBooth</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-xs text-gray-400 truncate max-w-[120px]">{user?.name}</span>
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-[#1A1A1A] text-white text-xs">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto pt-14 md:pt-0 pb-20 md:pb-0">
          <ErrorBoundary scope="this page">
            {children}
          </ErrorBoundary>
          {user?.client_type !== 'ai' && (
            <footer className="text-center text-[10px] text-gray-700 px-6 pt-8 pb-6 leading-relaxed max-w-3xl mx-auto" data-testid="app-legal-footer">
              <p>
                MusicBooth streams music via the official YouTube IFrame Player using
                the venue&apos;s YouTube Data API v3 key. We do not host, store, or
                rebroadcast any audio content. &quot;YouTube&quot; is a trademark of Google LLC.
              </p>
            </footer>
          )}
        </main>
      </div>

      {/* ===== Phone bottom tabs (<md) ===== */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 h-16 bg-black/95 backdrop-blur-md border-t border-white/8 flex items-stretch"
        data-testid="venue-bottom-tabs"
      >
        {phoneTopNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `
              flex-1 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider font-bold transition-colors
              ${isActive ? 'text-[#FF007F]' : 'text-gray-500'}
            `}
            data-testid={`bottom-tab-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-white transition-colors"
          data-testid="bottom-tab-more"
        >
          <MoreHorizontal className="w-5 h-5" />
          <span>More</span>
        </button>
      </nav>

      {/* ===== Phone "More" drawer ===== */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex flex-col justify-end" data-testid="venue-more-drawer">
          <button
            type="button"
            aria-label="Close drawer"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative bg-[#0D0D0D] border-t border-white/10 rounded-t-3xl p-5 pb-8 max-h-[70vh] overflow-y-auto animate-in slide-in-from-bottom-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-[0.35em] text-gray-500 font-['JetBrains_Mono']">More</p>
              <button onClick={() => setDrawerOpen(false)} className="text-gray-500 hover:text-white" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-1">
              {phoneMoreNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                    ${isActive ? 'bg-[#FF007F]/15 text-white ring-1 ring-[#FF007F]/30' : 'text-gray-300 hover:bg-white/5'}
                  `}
                  data-testid={`drawer-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-white/5">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10"
                data-testid="drawer-logout"
              >
                <LogOut className="w-5 h-5" />
                Log out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global PlayerBar — renders once at app shell level so every venue page
          keeps a visible mini-player and music controls across navigation.
          On the Control Booth home (mobile/tablet) it's hidden because that
          page now shows a full album-art player inline. */}
      <div className={hidePlayerBarBelowXl ? 'hidden xl:block' : ''}>
        <PlayerBar />
      </div>
    </div>
  );
};

export default Layout;
